import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LogOut, Search, FileText, Bookmark } from 'lucide-react';
import { Button } from './ui/button';
import MobileDrawerMenu from './MobileDrawerMenu';

const Header = () => {
  const { user, logout } = useAuth();
  const { language, switchLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: mobile hamburger + logo */}
          <div className="flex items-center gap-2">
            <MobileDrawerMenu />
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="header-logo">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Search className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="text-lg sm:text-xl font-bold text-slate-900">SafeSourcer</span>
            </Link>
          </div>

          {/* Center: desktop-only primary nav */}
          <nav className="hidden md:flex items-center gap-1 ml-6 mr-auto">
            <Link
              to="/dashboard"
              className="px-3 py-1.5 text-sm font-medium text-slate-600 rounded-md hover:bg-slate-100 hover:text-slate-900 transition-colors"
              data-testid="nav-dashboard"
            >
              {t('dashboard')}
            </Link>
            <Link
              to="/templates"
              className="px-3 py-1.5 text-sm font-medium text-slate-600 rounded-md hover:bg-slate-100 hover:text-slate-900 transition-colors flex items-center gap-1.5"
              data-testid="nav-templates"
            >
              <FileText className="w-4 h-4" />
              {t('templatesNav')}
            </Link>
            <Link
              to="/saved"
              className="px-3 py-1.5 text-sm font-medium text-slate-600 rounded-md hover:bg-slate-100 hover:text-slate-900 transition-colors flex items-center gap-1.5"
              data-testid="nav-saved"
            >
              <Bookmark className="w-4 h-4" />
              {t('savedNav')}
            </Link>
          </nav>

          {/* Right: desktop-only language, user, logout. Mobile hides all of this (moved into drawer). */}
          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1">
              <button
                onClick={() => switchLanguage('fr')}
                className={`px-3 py-1 text-sm rounded transition-all duration-200 ${
                  language === 'fr'
                    ? 'bg-blue-600 text-white font-medium'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="lang-fr-button"
              >
                FR
              </button>
              <button
                onClick={() => switchLanguage('en')}
                className={`px-3 py-1 text-sm rounded transition-all duration-200 ${
                  language === 'en'
                    ? 'bg-blue-600 text-white font-medium'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="lang-en-button"
              >
                EN
              </button>
            </div>

            <span className="text-sm text-slate-600">{user?.name}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="transition-all duration-200"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
